import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Bot,
  ClipboardList,
  Copy,
  CornerDownLeft,
  Database,
  FileDown,
  FileText,
  MapPin,
  Package,
  Truck,
  Undo2,
  Warehouse,
} from 'lucide-react';
import { api } from '@/lib/api';
import { VisualizarDanfe } from './VisualizarDanfe';
import { cn, moeda, numero } from '@/lib/utils';
import type { PedidoEncontrado } from '@/lib/tipos';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import { LinhasEsqueleto } from '@/componentes/ui/primitivos';

/**
 * RAIO-X DO PEDIDO — a resposta inteira para quem digitou uma televenda.
 *
 * De onde veio (canal, pedido do marketplace), o que tem (produtos com foto),
 * por onde passou (cada troca de status, com data, hora e QUEM mexeu), em que
 * pé está, a separação, a nota e o romaneio — sem abrir o Procfit ao lado.
 *
 * Abre já com o que a busca trouxe (status, canal, nota) e completa quando o
 * raio-X chega: quem está no telefone com o cliente vê algo na hora.
 */

interface RaioX {
  televenda: number;
  empresa: number;
  dataHora: string | null;
  canal: string | null;
  pedidoMarketplace: string | null;
  cliente: string | null;
  cidade: string | null;
  uf: string | null;
  estado: number | null;
  estadoDescricao: string | null;
  estadoDesde: string | null;
  entregueEm: string | null;
  previsaoEntrega: string | null;
  totalGeral: number | null;
  taxaEntrega: number | null;
  transportadora: string | null;
  historico: {
    dataHora: string;
    estado: number | null;
    descricao: string | null;
    usuario: string | null;
    origem: 'ERP' | 'WMS' | 'Automático' | 'Outro';
  }[];
  produtos: {
    produto: number;
    descricao: string | null;
    ean: string | null;
    quantidade: number;
    valorUnitario: number | null;
    total: number | null;
  }[];
  separacao: {
    lista: number;
    dataHora: string | null;
    usuario: string | null;
  }[];
  notas: {
    id: number;
    numero: number;
    serie: string | null;
    emissao: string | null;
    total: number | null;
    chave: string | null;
    situacao: string | null;
    canceladaEm: string | null;
    temXml: boolean;
    temXmlCancelamento: boolean;
  }[];
  devolucoes: {
    id: number;
    numero: number | null;
    serie: string | null;
    dataHora: string | null;
    notaDevolvida: number | null;
    usuario: string | null;
    situacao: string | null;
    temXml: boolean;
  }[];
  romaneios: {
    romaneio: number;
    dataHora: string | null;
    previsaoSaida: string | null;
    usuario: string | null;
  }[];
  ocorrencias: {
    dataHora: string | null;
    descricao: string | null;
    usuario: string | null;
  }[];
  cancelamentos: {
    dataHora: string | null;
    motivo: string | null;
    usuario: string | null;
  }[];
}

/** `aaaa-mm-dd hh:mm:ss` -> `dd/mm/aaaa hh:mm`. Sem fuso: o ERP grava hora local. */
export function quando(v: string | null, comHora = true, segundos = false): string {
  if (!v) return '—';
  const d = `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}`;
  if (!comHora || v.length <= 10) return d;
  if (segundos) return `${d} ${v.slice(11, 19)}`;
  return v.slice(11, 16) !== '00:00' ? `${d} ${v.slice(11, 16)}` : d;
}

const ORIGEM: Record<
  RaioX['historico'][number]['origem'],
  { rotulo: string; icone: JSX.Element; classe: string }
> = {
  ERP: {
    rotulo: 'ERP',
    icone: <Database className="h-3 w-3" />,
    classe: 'bg-superficie3 text-textoFraco',
  },
  WMS: {
    rotulo: 'WMS',
    icone: <Warehouse className="h-3 w-3" />,
    classe: 'bg-primaria-suave text-primaria',
  },
  Automático: {
    rotulo: 'automático',
    icone: <Bot className="h-3 w-3" />,
    classe: 'bg-superficie3 text-textoFraco',
  },
  Outro: {
    rotulo: 'outro sistema',
    icone: <Database className="h-3 w-3" />,
    classe: 'bg-superficie3 text-textoFraco',
  },
};

export function Secao({
  titulo,
  icone,
  children,
  className,
}: {
  titulo: string;
  icone: JSX.Element;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={cn('rounded-xl border border-borda bg-superficie p-3', className)}>
      <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-textoFraco">
        {icone}
        {titulo}
      </h3>
      {children}
    </section>
  );
}

export function SituacaoNf({ situacao }: { situacao: string | null }): JSX.Element | null {
  if (!situacao) return null;
  return (
    <span
      className={cn(
        'rounded-md px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide',
        situacao === 'NF-e AUTORIZADA'
          ? 'bg-status-livreFundo text-status-livre'
          : situacao === 'CANCELADA'
            ? 'bg-status-divergencia text-white'
            : 'bg-status-divergenciaFundo text-status-divergencia',
      )}
    >
      {situacao.replace('NF-e ', '')}
    </span>
  );
}

const CLASSE_ARQUIVO =
  'inline-flex items-center gap-1 rounded-md border border-borda bg-superficie px-1.5 py-0.5 text-[11px] font-semibold text-textoFraco transition-colors hover:border-primaria hover:text-primaria';

/**
 * Baixa o XML como o ERP gravou. Link direto: a sessão é cookie, e o nome do
 * arquivo (a chave da nota) vem do servidor.
 *
 * Com `pdf`, é o DANFE: abre a pré-visualização, com Imprimir e Baixar, em vez
 * de baixar às cegas. Leva o ícone de documento em vermelho, a cor que todo
 * mundo associa a PDF, para não ser confundido com o XML ao lado.
 */
export function BaixarXml({ href, rotulo, pdf = false }: { href: string; rotulo: string; pdf?: boolean }): JSX.Element {
  const [vendo, definirVendo] = React.useState(false);
  if (pdf) {
    return (
      <>
        <button type="button" title={`Ver ${rotulo}`} onClick={() => definirVendo(true)} className={CLASSE_ARQUIVO}>
          <FileText className="h-3.5 w-3.5 text-red-600" />
          {rotulo}
        </button>
        {vendo ? <VisualizarDanfe href={href} titulo={rotulo} aoFechar={() => definirVendo(false)} /> : null}
      </>
    );
  }
  return (
    <a href={href} download title={`Baixar ${rotulo}`} className={CLASSE_ARQUIVO}>
      <FileDown className="h-3.5 w-3.5" />
      {rotulo}
    </a>
  );
}

export function RaioXPedido({
  pedido,
  aoVoltar,
  aoAbrirRomaneio,
  aoAbrirSeparacao,
}: {
  /* Só pedido de verdade abre o raio-X: nota sem televenda fica na lista. */
  pedido: PedidoEncontrado & { televenda: number };
  aoVoltar: () => void;
  aoAbrirRomaneio: (romaneio: number) => void;
  aoAbrirSeparacao: (lista: number) => void;
}): JSX.Element {
  const consulta = useQuery({
    queryKey: ['pedido', pedido.televenda, 'raio-x'],
    queryFn: () => api.get<RaioX>(`/api/pedidos/${pedido.televenda}/raio-x`),
    staleTime: 30_000,
  });
  const r = consulta.data;
  const estado = r?.estadoDescricao ?? pedido.estadoDescricao;
  const historico = r ? [...r.historico].reverse() : [];

  return (
    <div className="p-1.5">
      <button
        type="button"
        onClick={aoVoltar}
        className="mb-2 flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold text-primaria transition-colors hover:bg-primaria-suave"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Voltar aos resultados
      </button>

      {/* Cabeçalho: quem, de onde, em que pé está. */}
      <div className="rounded-xl border border-borda bg-superficie2 p-3.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="tabular text-[20px] font-bold tracking-[-0.02em]">
                Televenda {pedido.televenda}
              </span>
              <span className="text-[12px] text-textoFraco">empresa {pedido.empresa}</span>
              <span className="tabular text-[12px] text-textoFraco">
                {quando(r?.dataHora ?? pedido.dataHora, false)}
              </span>
            </p>
            {pedido.cliente ? (
              <p className="mt-0.5 flex items-center gap-1 truncate text-[13px] text-textoFraco">
                {pedido.cliente}
                {r?.cidade ? (
                  <>
                    <MapPin className="ml-1.5 h-3 w-3" />
                    {r.cidade}
                    {r.uf ? `/${r.uf}` : ''}
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          <div className="text-right">
            <span className="inline-block rounded-lg bg-primaria px-2.5 py-1 text-[13px] font-bold text-white">
              {estado ?? 'sem status'}
            </span>
            {r?.estadoDesde ? (
              <p className="tabular mt-1 text-[11px] text-textoFraco">desde {quando(r.estadoDesde)}</p>
            ) : null}
          </div>
        </div>
        <dl className="mt-3 grid gap-2 text-[13px] sm:grid-cols-4">
          <Dado rotulo="Canal">{r?.canal ?? pedido.canal ?? '—'}</Dado>
          <Dado rotulo="Pedido no marketplace">
            {(r?.pedidoMarketplace ?? pedido.pedidoMarketplace) ? (
              <button
                type="button"
                title="Copiar"
                onClick={() =>
                  void navigator.clipboard?.writeText(r?.pedidoMarketplace ?? pedido.pedidoMarketplace ?? '')
                }
                className="tabular inline-flex items-center gap-1 hover:text-primaria"
              >
                {r?.pedidoMarketplace ?? pedido.pedidoMarketplace}
                <Copy className="h-3 w-3 opacity-60" />
              </button>
            ) : (
              '—'
            )}
          </Dado>
          <Dado rotulo="Total">
            {r?.totalGeral != null ? moeda(r.totalGeral) : '—'}
            {r?.taxaEntrega ? (
              <span className="text-[11px] text-textoFraco"> · frete {moeda(r.taxaEntrega)}</span>
            ) : null}
          </Dado>
          <Dado rotulo={r?.entregueEm ? 'Entregue em' : 'Previsão de entrega'}>
            {r?.entregueEm ? quando(r.entregueEm) : quando(r?.previsaoEntrega ?? null, false)}
            {r?.transportadora ? (
              <span className="block text-[11px] text-textoFraco">{r.transportadora}</span>
            ) : null}
          </Dado>
        </dl>
      </div>

      {consulta.isLoading ? (
        <div className="p-4">
          <LinhasEsqueleto linhas={4} />
        </div>
      ) : consulta.isError || !r ? (
        <p className="mt-3 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
          Não foi possível carregar o raio-X do pedido.
        </p>
      ) : (
        <>
          <div className="mt-2.5 grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-2.5">
              <Secao titulo={`Produtos (${r.produtos.length})`} icone={<Package className="h-3.5 w-3.5" />}>
                <ul className="divide-y divide-borda">
                  {r.produtos.map((p, i) => (
                    <li
                      key={`${p.produto}-${i}`}
                      className="flex items-center gap-3 py-2 first:pt-0 last:pb-0"
                    >
                      <ImagemProduto sku={p.produto} tamanho={52} alt={p.descricao ?? ''} />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-[13px] font-medium leading-snug">{p.descricao}</p>
                        <p className="tabular text-[11px] text-textoFraco">
                          SKU {p.produto}
                          {p.ean ? ` · EAN ${p.ean}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="tabular text-[14px] font-bold">{numero(p.quantidade)} un</p>
                        {p.total != null ? (
                          <p className="tabular text-[11px] text-textoFraco">{moeda(p.total)}</p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </Secao>
            </div>

            <div className="space-y-2.5">
              <div className="space-y-2.5">
                <Secao titulo="Separação" icone={<ClipboardList className="h-3.5 w-3.5" />}>
                  {r.separacao.length === 0 ? (
                    <p className="text-[12.5px] text-textoFraco">Ainda não entrou em lista de separação.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {r.separacao.map((sep) => (
                        <li key={sep.lista}>
                          <button
                            type="button"
                            onClick={() => aoAbrirSeparacao(sep.lista)}
                            title="Abrir a lista na Separação (etiquetas emitidas)"
                            className="group flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[12.5px] transition-colors hover:bg-primaria-suave"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="tabular font-bold group-hover:text-primaria">
                                Lista {sep.lista}
                              </span>
                              <span className="block text-[11px] text-textoFraco">
                                {quando(sep.dataHora)}
                                {sep.usuario ? ` · ${sep.usuario}` : ''}
                              </span>
                            </span>
                            <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-textoSuave group-hover:text-primaria" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Secao>

                <Secao
                  titulo={r.notas.length > 1 ? `Notas fiscais (${r.notas.length})` : 'Nota fiscal'}
                  icone={<FileText className="h-3.5 w-3.5" />}
                >
                  {r.notas.length === 0 ? (
                    <p className="text-[12.5px] text-textoFraco">Ainda não faturado.</p>
                  ) : (
                    <ul className="space-y-2">
                      {r.notas.map((n) => (
                        <li
                          key={n.id}
                          className={cn('text-[12.5px]', n.situacao === 'CANCELADA' && 'opacity-80')}
                        >
                          <span className="flex flex-wrap items-center gap-1.5">
                            {/* Número de nota é identificador: sem separador de milhar. */}
                            <span
                              className={cn(
                                'tabular font-bold',
                                n.situacao === 'CANCELADA' && 'line-through',
                              )}
                            >
                              {n.numero}
                              {n.serie ? ` · série ${n.serie}` : ''}
                            </span>
                            <SituacaoNf situacao={n.situacao} />
                          </span>
                          <span className="block text-[11px] text-textoFraco">
                            emitida {quando(n.emissao)}
                            {n.total ? ` · ${moeda(n.total)}` : ''}
                            {n.canceladaEm ? ` · cancelada ${quando(n.canceladaEm)}` : ''}
                          </span>
                          {n.temXml || n.temXmlCancelamento ? (
                            <span className="mt-1 flex flex-wrap gap-1.5">
                              {n.temXml ? (
                                <>
                                  <BaixarXml href={`/api/pedidos/notas/${n.id}/pdf`} rotulo="DANFE" pdf />
                                  <BaixarXml href={`/api/pedidos/notas/${n.id}/xml`} rotulo="XML" />
                                </>
                              ) : null}
                              {n.temXmlCancelamento ? (
                                <BaixarXml
                                  href={`/api/pedidos/notas/${n.id}/xml?tipo=cancelamento`}
                                  rotulo="XML cancelamento"
                                />
                              ) : null}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </Secao>
              </div>

              {r.devolucoes.length > 0 ? (
                <Secao
                  titulo={r.devolucoes.length > 1 ? `Devoluções (${r.devolucoes.length})` : 'Devolução'}
                  icone={<Undo2 className="h-3.5 w-3.5 text-status-atencao" />}
                  className="border-status-atencao/40"
                >
                  <ul className="space-y-2">
                    {r.devolucoes.map((d) => (
                      <li key={d.id} className="text-[12.5px]">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="tabular font-bold">
                            NFD {d.numero ?? '—'}
                            {d.serie ? ` · série ${d.serie}` : ''}
                          </span>
                          <SituacaoNf situacao={d.situacao} />
                        </span>
                        <span className="block text-[11px] text-textoFraco">
                          {quando(d.dataHora)}
                          {d.notaDevolvida ? ` · devolve a NF ${d.notaDevolvida}` : ''}
                          {d.usuario ? ` · ${d.usuario}` : ''}
                        </span>
                        {d.temXml ? (
                          <span className="mt-1 flex flex-wrap gap-1.5">
                            <BaixarXml href={`/api/pedidos/devolucoes/${d.id}/pdf`} rotulo="DANFE da NFD" pdf />
                            <BaixarXml href={`/api/pedidos/devolucoes/${d.id}/xml`} rotulo="XML da NFD" />
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </Secao>
              ) : null}

              <Secao titulo="Romaneio" icone={<Truck className="h-3.5 w-3.5" />}>
                {r.romaneios.length === 0 ? (
                  <p className="text-[12.5px] text-textoFraco">
                    Ainda não está em romaneio — a carga não foi montada.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {r.romaneios.map((x) => (
                      <button
                        key={x.romaneio}
                        type="button"
                        onClick={() => aoAbrirRomaneio(x.romaneio)}
                        className="flex w-full items-center gap-3 rounded-lg border border-primaria/30 bg-primaria-suave px-3 py-2 text-left transition-colors hover:bg-primaria/10"
                      >
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primaria text-white">
                          <Truck className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="tabular block text-[13.5px] font-bold">Romaneio {x.romaneio}</span>
                          <span className="block text-[11px] text-textoFraco">
                            {quando(x.dataHora)}
                            {x.previsaoSaida ? ` · saída prevista ${quando(x.previsaoSaida, false)}` : ''}
                            {x.usuario ? ` · ${x.usuario}` : ''}
                          </span>
                        </span>
                        <CornerDownLeft className="h-4 w-4 shrink-0 text-primaria" />
                      </button>
                    ))}
                  </div>
                )}
              </Secao>

              {r.cancelamentos.length > 0 ? (
                <Secao
                  titulo="Cancelamento"
                  icone={<Ban className="h-3.5 w-3.5 text-status-divergencia" />}
                  className="border-status-divergencia/40"
                >
                  <ul className="space-y-1.5 text-[12.5px]">
                    {r.cancelamentos.map((c, i) => (
                      <li key={i}>
                        <span className="font-semibold">{c.motivo ?? 'Sem motivo informado'}</span>
                        <span className="block text-[11px] text-textoFraco">
                          {quando(c.dataHora)}
                          {c.usuario ? ` · ${c.usuario}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Secao>
              ) : null}

              {r.ocorrencias.length > 0 ? (
                <Secao
                  titulo={`Ocorrências (${r.ocorrencias.length})`}
                  icone={<AlertTriangle className="h-3.5 w-3.5" />}
                >
                  <ul className="space-y-1.5 text-[12.5px]">
                    {[...r.ocorrencias].reverse().map((o, i) => (
                      <li key={i}>
                        <span className="font-medium">{o.descricao ?? 'Ocorrência'}</span>
                        <span className="block text-[11px] text-textoFraco">
                          {quando(o.dataHora)}
                          {o.usuario ? ` · ${o.usuario}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Secao>
              ) : null}
            </div>
          </div>
          <Secao titulo="Histórico de status" icone={<Database className="h-3.5 w-3.5" />} className="mt-2.5">
            {historico.length === 0 ? (
              <p className="text-[12.5px] text-textoFraco">Sem trocas de status registradas.</p>
            ) : (
              <table className="w-full table-fixed text-[12.5px]">
                <colgroup>
                  <col className="w-[30%]" />
                  <col />
                  <col className="w-[112px]" />
                  <col className="w-[150px]" />
                </colgroup>
                <thead className="text-left text-[10.5px] uppercase tracking-wide text-textoFraco">
                  <tr>
                    <th className="pb-1.5 font-semibold">Status</th>
                    <th className="pb-1.5 font-semibold">Usuário</th>
                    <th className="pb-1.5 font-semibold">Origem</th>
                    <th className="pb-1.5 text-right font-semibold">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((h, i) => {
                    const o = ORIGEM[h.origem];
                    return (
                      <tr
                        key={`${h.dataHora}-${i}`}
                        className={cn('border-t border-borda', i === 0 && 'bg-primaria-suave/50')}
                      >
                        <td
                          className={cn(
                            'truncate py-1.5 pl-1 pr-2',
                            i === 0 ? 'font-bold text-primaria' : 'font-medium',
                          )}
                        >
                          {h.descricao ?? `Estado ${h.estado ?? '?'}`}
                        </td>
                        <td className="truncate py-1.5 pr-2" title={h.usuario ?? ''}>
                          {h.usuario ?? (h.origem === 'Automático' ? 'processo automático' : '—')}
                        </td>
                        <td className="py-1.5 pr-2">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded px-1.5 py-px text-[10.5px] font-semibold',
                              o.classe,
                            )}
                          >
                            {o.icone}
                            {o.rotulo}
                          </span>
                        </td>
                        <td className="tabular whitespace-nowrap py-1.5 pr-1 text-right text-textoFraco">
                          {quando(h.dataHora, true, true)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Secao>
        </>
      )}
    </div>
  );
}

export function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-textoFraco">{rotulo}</dt>
      <dd className="truncate">{children}</dd>
    </div>
  );
}
